/**
 * Pointer-capability checks. The media queries describe the PRIMARY pointing
 * device and never flip per interaction — a touchscreen laptop reports
 * hover:hover even while the user is tapping with a finger. For per-event
 * truth use `lastPressWasTouch()`, which tracks the pointerType of the most
 * recent pointerdown. SSR-safe: everything degrades to "no hover / not touch"
 * without a window.
 *
 * Use `hasHover()` to gate hover-open affordances — on touch-primary devices,
 * taps synthesize a mouseenter BEFORE click, so an ungated hover-open handler
 * fights the click toggle (open→instantly-closed). Use `lastPressWasTouch()`
 * where the CURRENT interaction matters (autofocus after a tap pops the
 * keyboard over the UI even on hover-capable hybrids; and, pre-CSS-guard,
 * focusing a <16px input triggered the iOS page zoom).
 */
export const hasHover = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export const hasFinePointer = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: fine)").matches;

// Module-level per-event tracker: one capture-phase document listener records
// the pointerType of every pointerdown. Capture so it runs before any
// stopPropagation in component handlers.
let lastPointerType: string | null = null;
if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener(
    "pointerdown",
    (e: PointerEvent) => {
      lastPointerType = e.pointerType;
    },
    { capture: true, passive: true },
  );
}

/**
 * True when the most recent press was a touch (finger). Falls back to the
 * primary-pointer media query when no press has happened yet (fresh load,
 * keyboard-only session).
 */
export const lastPressWasTouch = (): boolean =>
  lastPointerType !== null ? lastPointerType === "touch" : !hasFinePointer();
